import {
  consumeRagSse,
  ragFetch,
  ragUpload,
  fetchDocumentFile,
  fetchDocumentVersionFile,
  fetchThreadAttachmentFile,
} from "@/lib/api/rag-client";
import { pollUntilTerminal } from "./processing-poll.js";
import type {
  ApiChatHistoryResponse,
  ApiDocument,
  ApiLibrary,
  ApiLibraryChange,
  ApiProcessingStatus,
  ApiSendMessageResponse,
  ApiChatStreamDonePayload,
  ApiChatMessage,
  ApiChatSource,
  RagChatStreamCallbacks,
  RagRetrievalMode,
  RagStreamStatus,
  ApiRagCapabilities,
  ApiThread,
  ApiThreadAttachment,
  ApiWorkspace,
  ApiWorkspaceRagGuideline,
  ApiLibraryRagGuideline,
  ApiWorkspaceUser,
} from "./types.js";

export async function getRagMe(): Promise<{ user: { id: string; email: string } }> {
  return ragFetch("/api/auth/me");
}

export async function listWorkspaces(): Promise<{ workspaces: ApiWorkspace[] }> {
  return ragFetch("/api/workspaces");
}

export async function createWorkspace(body: {
  name: string;
  description?: string;
}): Promise<{ workspace: ApiWorkspace }> {
  return ragFetch("/api/workspaces", {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function getWorkspace(id: string): Promise<{ workspace: ApiWorkspace }> {
  return ragFetch(`/api/workspaces/${id}`, { cache: "no-store" });
}

export async function updateWorkspace(
  id: string,
  body: { name?: string; description?: string },
): Promise<{ workspace: ApiWorkspace }> {
  return ragFetch(`/api/workspaces/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function deleteWorkspace(id: string): Promise<{ success: boolean }> {
  return ragFetch(`/api/workspaces/${id}`, {
    method: "DELETE",
  });
}

export async function getWorkspaceRagGuideline(
  workspaceId: string,
): Promise<ApiWorkspaceRagGuideline> {
  return ragFetch(`/api/workspaces/${workspaceId}/rag-guideline`);
}

export async function updateWorkspaceRagGuideline(
  workspaceId: string,
  guideline: string | null,
): Promise<ApiWorkspaceRagGuideline> {
  return ragFetch(`/api/workspaces/${workspaceId}/rag-guideline`, {
    method: "PUT",
    body: JSON.stringify({ guideline }),
  });
}

export async function getLibraryRagGuideline(
  libraryId: string,
): Promise<ApiLibraryRagGuideline> {
  return ragFetch(`/api/libraries/${libraryId}/rag-guideline`);
}

export async function updateLibraryRagGuideline(
  libraryId: string,
  guideline: string | null,
): Promise<ApiLibraryRagGuideline> {
  return ragFetch(`/api/libraries/${libraryId}/rag-guideline`, {
    method: "PUT",
    body: JSON.stringify({ guideline }),
  });
}

export async function addWorkspaceMember(
  workspaceId: string,
  body: { userId?: string; email?: string; role?: "ADMIN" | "MEMBER" | "VIEWER" },
): Promise<{ workspaceUser: ApiWorkspaceUser }> {
  return ragFetch(`/api/workspaces/${workspaceId}/users`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function removeWorkspaceMember(
  workspaceId: string,
  userId: string,
): Promise<{ success: boolean }> {
  return ragFetch(`/api/workspaces/${workspaceId}/users/${userId}`, {
    method: "DELETE",
  });
}

export async function listLibraries(
  workspaceId: string
): Promise<{ libraries: ApiLibrary[] }> {
  return ragFetch(`/api/libraries/workspace/${workspaceId}`);
}

export async function getLibrary(id: string): Promise<{ library: ApiLibrary }> {
  return ragFetch(`/api/libraries/${id}`);
}

/** Persistent per-user library for General Chat uploads (get-or-create). */
export async function ensureGeneralLibrary(): Promise<{ library: ApiLibrary }> {
  return ragFetch("/api/libraries/general");
}

export async function listPublicLibraries(): Promise<{ libraries: ApiLibrary[] }> {
  return ragFetch("/api/libraries/public");
}

export async function listLibraryChanges(
  libraryId: string,
  options?: { page?: number; limit?: number; syncRunId?: string; documentId?: string },
): Promise<{ changes: ApiLibraryChange[]; page: number; limit: number; total: number }> {
  const params = new URLSearchParams();
  params.set("page", String(options?.page ?? 1));
  params.set("limit", String(options?.limit ?? 25));
  if (options?.syncRunId?.trim()) {
    params.set("syncRunId", options.syncRunId.trim());
  }
  if (options?.documentId?.trim()) {
    params.set("documentId", options.documentId.trim());
  }
  const data = await ragFetch<{
    changes?: ApiLibraryChange[];
    page?: number;
    limit?: number;
    total?: number;
  }>(`/api/libraries/${libraryId}/changes?${params.toString()}`);
  return {
    changes: Array.isArray(data.changes) ? data.changes : [],
    page: data.page ?? options?.page ?? 1,
    limit: data.limit ?? options?.limit ?? 25,
    total: data.total ?? 0,
  };
}

export async function createLibrary(
  workspaceId: string,
  body: { name: string; description?: string }
): Promise<{ library: ApiLibrary }> {
  return ragFetch(`/api/libraries/workspace/${workspaceId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function updateLibrary(
  id: string,
  body: { name?: string; description?: string },
): Promise<{ library: ApiLibrary }> {
  return ragFetch(`/api/libraries/${id}`, {
    method: "PUT",
    body: JSON.stringify(body),
  });
}

export async function deleteLibrary(id: string): Promise<{ success: boolean }> {
  return ragFetch(`/api/libraries/${id}`, {
    method: "DELETE",
  });
}

export async function listDocuments(
  libraryId: string,
  options?: { page?: number; limit?: number; q?: string },
): Promise<{ documents: ApiDocument[]; page: number; limit: number; total: number }> {
  const params = new URLSearchParams();
  params.set("page", String(options?.page ?? 1));
  params.set("limit", String(options?.limit ?? 25));
  if (options?.q?.trim()) {
    params.set("q", options.q.trim());
  }
  const data = await ragFetch<{
    documents?: ApiDocument[];
    page?: number;
    limit?: number;
    total?: number;
  }>(`/api/documents/library/${libraryId}?${params.toString()}`);
  return {
    documents: Array.isArray(data.documents) ? data.documents : [],
    page: data.page ?? options?.page ?? 1,
    limit: data.limit ?? options?.limit ?? 25,
    total: data.total ?? 0,
  };
}

export async function getDocumentStatus(id: string): Promise<{
  documentId: string;
  processingStatus: ApiDocument["processingStatus"];
}> {
  return ragFetch(`/api/documents/${id}`);
}

export async function getDocumentProcessingStatus(id: string): Promise<{
  documentId: string;
  processingStatus: ApiDocument["processingStatus"];
  conversionStatus: string | null;
  conversionTaskId: string | null;
  queuePosition: number | null;
  elapsedSeconds: number | null;
  numPages: number | null;
  numChunks: number | null;
  error: string | null;
}> {
  return ragFetch(`/api/documents/${id}/status`);
}

export async function getDocumentDownloadUrl(documentId: string): Promise<{
  downloadUrl: string;
  title: string;
  mimeType: string | null;
}> {
  return ragFetch(`/api/documents/${documentId}/download-url`);
}

export async function getDocumentVersionDownloadUrl(
  documentId: string,
  versionId: string,
): Promise<{
  downloadUrl: string;
  title: string;
  mimeType: string | null;
  documentVersionId: string;
  contentHash: string | null;
}> {
  return ragFetch(`/api/documents/${documentId}/versions/${versionId}/download-url`);
}

export { fetchDocumentFile, fetchDocumentVersionFile, fetchThreadAttachmentFile };

export async function getThreadAttachmentDownloadUrl(
  threadId: string,
  attachmentId: string,
): Promise<{
  downloadUrl: string;
  title: string;
  mimeType: string | null;
}> {
  return ragFetch(`/api/threads/${threadId}/attachments/${attachmentId}/download-url`);
}

export async function deleteDocument(documentId: string): Promise<{ success: boolean }> {
  return ragFetch(`/api/documents/${documentId}`, { method: "DELETE" });
}

export async function reprocessDocument(documentId: string): Promise<{ success: boolean }> {
  return ragFetch(`/api/documents/${documentId}/reprocess`, { method: "POST" });
}

export async function uploadDocument(
  libraryId: string,
  file: File,
  title?: string
): Promise<{ document: ApiDocument }> {
  const formData = new FormData();
  formData.append("file", file);
  if (title?.trim()) {
    formData.append("title", title.trim());
  }
  return ragUpload(`/api/documents/library/${libraryId}/upload`, formData);
}

export async function attachThreadDocument(
  threadId: string,
  documentId: string
): Promise<{
  threadDocument: {
    id: string;
    threadId: string;
    documentId: string;
    document?: { id: string; title: string };
  };
}> {
  return ragFetch(`/api/threads/${threadId}/documents`, {
    method: "POST",
    body: JSON.stringify({ documentId }),
  });
}

export async function detachThreadDocument(
  threadId: string,
  documentId: string
): Promise<{ success: boolean }> {
  return ragFetch(`/api/threads/${threadId}/documents/${documentId}`, {
    method: "DELETE",
  });
}

export async function listThreads(
  libraryId: string
): Promise<{ threads: ApiThread[] }> {
  return ragFetch(`/api/threads/library/${libraryId}`);
}

export async function listWorkspaceThreads(
  workspaceId: string
): Promise<{ threads: ApiThread[] }> {
  return ragFetch(`/api/threads/workspace/${workspaceId}`);
}

export async function listPublicThreads(): Promise<{ threads: ApiThread[] }> {
  return ragFetch("/api/threads/public");
}

export async function createThread(
  libraryId: string,
  body: { title: string }
): Promise<{ thread: ApiThread }> {
  return ragFetch(`/api/threads/library/${libraryId}`, {
    method: "POST",
    body: JSON.stringify(body),
  });
}

export async function createWorkspaceThread(
  workspaceId: string,
  body: { title: string; libraryIds?: string[] }
): Promise<{ thread: ApiThread }> {
  return ragFetch(`/api/threads/workspace/${workspaceId}`, {
    method: "POST",
    body: JSON.stringify({
      title: body.title,
      libraryIds: body.libraryIds ?? [],
    }),
  });
}

export async function createPublicThread(body: {
  title: string;
  libraryIds?: string[];
}): Promise<{ thread: ApiThread }> {
  return ragFetch("/api/threads/public", {
    method: "POST",
    body: JSON.stringify({
      title: body.title,
      libraryIds: body.libraryIds ?? [],
    }),
  });
}

export async function updateThreadLibraries(
  threadId: string,
  libraryIds: string[]
): Promise<{ thread: ApiThread }> {
  return ragFetch(`/api/threads/${threadId}/libraries`, {
    method: "PUT",
    body: JSON.stringify({ libraryIds }),
  });
}

export async function getThread(id: string): Promise<{ thread: ApiThread }> {
  return ragFetch(`/api/threads/${id}`);
}

export async function deleteThread(id: string): Promise<{ success: boolean }> {
  return ragFetch(`/api/threads/${id}`, { method: "DELETE" });
}

export async function getChatHistory(
  threadId: string,
  page = 1,
  perPage = 50
): Promise<ApiChatHistoryResponse> {
  const params = new URLSearchParams({
    page: String(page),
    perPage: String(perPage),
  });
  return ragFetch(`/api/chats/thread/${threadId}?${params}`);
}

export async function setChatFeedback(
  chatId: string,
  rating: "positive" | "negative" | null
): Promise<{ rating: "positive" | "negative" | null }> {
  return ragFetch(`/api/chats/${encodeURIComponent(chatId)}/feedback`, {
    method: "PUT",
    body: JSON.stringify({ rating }),
  });
}

export async function getRagCapabilities(): Promise<ApiRagCapabilities> {
  return ragFetch("/api/chats/capabilities");
}

export async function sendMessage(
  threadId: string,
  message: string,
  options?: { retrievalMode?: RagRetrievalMode }
): Promise<ApiSendMessageResponse> {
  return ragFetch(`/api/chats/thread/${threadId}`, {
    method: "POST",
    body: JSON.stringify({ message, retrievalMode: options?.retrievalMode }),
  });
}

export async function sendMessageStream(
  threadId: string,
  message: string,
  callbacks: RagChatStreamCallbacks,
  options?: { signal?: AbortSignal; retrievalMode?: RagRetrievalMode },
): Promise<void> {
  await consumeRagSse(
    `/api/chats/thread/${threadId}/stream`,
    { message, retrievalMode: options?.retrievalMode },
    {
      onUser: (data) => callbacks.onUser?.(data as ApiChatMessage),
      onStatus: (data) => {
        if (data && typeof data === "object" && "message" in data) {
          callbacks.onStatus?.(data as RagStreamStatus);
        }
      },
      onSources: (data) => callbacks.onSources?.(data as ApiChatSource[]),
      onDelta: (data) => {
        if (typeof data === "string") {
          callbacks.onDelta?.(data);
        }
      },
      onDone: (data) => {
        const payload = data as ApiChatStreamDonePayload;
        callbacks.onDone?.(payload.assistantMessage);
      },
    },
    options?.signal,
  );
}

export async function uploadThreadAttachment(
  threadId: string,
  file: File,
  title?: string
): Promise<{ attachment: ApiThreadAttachment; message: string }> {
  const formData = new FormData();
  formData.append("file", file);
  if (title?.trim()) {
    formData.append("title", title.trim());
  }
  return ragUpload(`/api/threads/${threadId}/attachments`, formData);
}

export {
  ProcessingPollTimeoutError,
  PROCESSING_MAX_POLL_ATTEMPTS,
  PROCESSING_POLL_INTERVAL_MS,
  pollUntilTerminal,
} from "./processing-poll.js";

export async function getThreadAttachmentProcessingStatus(
  threadId: string,
  attachmentId: string,
): Promise<ApiProcessingStatus | null> {
  const { thread } = await getThread(threadId);
  return thread.attachments?.find((item) => item.id === attachmentId)?.processingStatus ?? null;
}

export async function getDocumentProcessingStatusValue(
  documentId: string,
): Promise<ApiProcessingStatus | null> {
  const status = await getDocumentStatus(documentId);
  return status.processingStatus;
}

export async function pollThreadAttachmentUntilDone(
  threadId: string,
  attachmentId: string,
  options?: {
    intervalMs?: number;
    maxAttempts?: number;
    onStatus?: (status: ApiProcessingStatus | null | undefined) => void;
  },
): Promise<ApiProcessingStatus> {
  return pollUntilTerminal(
    async () => {
      const { thread } = await getThread(threadId);
      return thread.attachments?.find((item) => item.id === attachmentId)?.processingStatus;
    },
    {
      intervalMs: options?.intervalMs,
      maxAttempts: options?.maxAttempts,
      onStatus: options?.onStatus,
    },
  );
}

export async function pollDocumentUntilDone(
  documentId: string,
  options?: {
    intervalMs?: number;
    maxAttempts?: number;
    onStatus?: (status: ApiProcessingStatus | null | undefined) => void;
  },
): Promise<ApiDocument["processingStatus"]> {
  return pollUntilTerminal(
    async () => {
      const status = await getDocumentStatus(documentId);
      return status.processingStatus;
    },
    {
      intervalMs: options?.intervalMs,
      maxAttempts: options?.maxAttempts,
      onStatus: options?.onStatus,
    },
  );
}
